import {
  Button,
  Classes,
  Dialog,
  HotkeyConfig,
  useHotkeys,
} from "@blueprintjs/core";
import classNames from "classnames";
import {
  FGCustomInput,
  IFGCustomInputProps,
  useFGContext,
} from "nsitools-react";
import { ClearButton } from "./ClearButton";
import * as React from "react";
import styled from "styled-components";

import { ContactSelectorPanel } from "./ContactSelectorPanel";
import { useCommonHooks } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { ContactGridDto } from "../../../api";
import { AddButton } from "../..";

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export interface INewComerContactSelectorFieldProps
  extends Omit<IFGCustomInputProps, "children" | "name"> {
  textName: string;
  idName: string;
  onNewContact?: () => void;
  disabled?: boolean;
}

export const NewComerContactSelectorField: React.FunctionComponent<
  INewComerContactSelectorFieldProps
> = ({
  textName,
  idName,
  label = null,
  disabled = false,
  className,
  onNewContact,
  ...fgTextInputProps
}) => {
  const { t } = useCommonHooks();
  const ctx = useFGContext();
  const [selectorDialogOpen, setSelectorDialogOpen] = React.useState(false);

  const openContactSelector = React.useCallback(() => {
    setSelectorDialogOpen(true);
  }, [setSelectorDialogOpen]);

  const setContactValues = React.useCallback(
    (idValue: number, textValue: string) => {
      ctx?.formik?.setFieldTouched(idName);
      ctx?.formik?.setFieldTouched(textName);
      ctx?.formik?.setFieldValue(idName, idValue, true);
      ctx?.formik?.setFieldValue(textName, textValue, true);
    },
    [ctx?.formik, idName, textName]
  );

  const onClosed = React.useCallback(
    (selectedContact?: ContactGridDto) => {
      if (selectedContact) {
        setContactValues(
          selectedContact.id,
          `${selectedContact.firstName ? selectedContact.firstName + " " : ""}${
            selectedContact.name
          }`
        );
      }
      setSelectorDialogOpen(false);
    },
    [setContactValues, setSelectorDialogOpen]
  );

  const clearSelectedContact = React.useCallback(() => {
    setContactValues(null, null);
  }, [setContactValues]);

  const hotkeys = React.useMemo<HotkeyConfig[]>(
    () => [
      {
        combo: "enter",
        group: t(ETLCodes.NewComerSelector),
        label: t(ETLCodes.OpenNewComerSelector),
        preventDefault: true,
        stopPropagation: true,
        allowInInput: true,
        onKeyDown: disabled ? null : openContactSelector,
      },
    ],
    [disabled, openContactSelector, t]
  );

  const { handleKeyDown } = useHotkeys(hotkeys);

  const buttons = React.useMemo(
    () => (
      <>
        {ctx?.formik?.values && !!ctx?.formik?.values[idName] && (
          <ClearButton onClick={clearSelectedContact} tabIndex={-1} />
        )}
        <Button
          icon="people"
          minimal={true}
          onClick={openContactSelector}
          disabled={disabled}
          tabIndex={-1}
        />
      </>
    ),
    [
      clearSelectedContact,
      ctx?.formik?.values,
      disabled,
      idName,
      openContactSelector,
    ]
  );

  const onAddClick = React.useCallback(async () => {
    setSelectorDialogOpen(false);
    onNewContact();
  }, [onNewContact]);

  return (
    <>
      <FGCustomInput
        name={textName}
        label={label}
        {...fgTextInputProps}
        readonly={!disabled}
        className={classNames({ "not-disabled": !disabled }, className)}
      >
        {(ctx) => (
          <InputContainer
            className={Classes.INPUT}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            {ctx.formik?.values?.[textName]}
            <div>{buttons}</div>
          </InputContainer>
        )}
      </FGCustomInput>
      <Dialog
        title={t(ETLCodes.SearchContact)}
        isOpen={selectorDialogOpen}
        onClose={() => onClosed(null)}
        style={{ width: "1200px", padding: "10px" }}
      >
        <ContactSelectorPanel
          onContactSelected={onClosed}
          rightElement={!!onNewContact && <AddButton onClick={onAddClick} />}
        />
      </Dialog>
    </>
  );
};
